import React from 'react';
import Button from 'components/Button';
import {useTranslation} from 'react-i18next';
import {usePostApi} from 'common/hooks/usePostApi';
import Dialog, {DialogContent} from 'components/Dialog';
import {DialogActions} from '@mui/material';
import {useModal} from 'common/hooks/useModal';
import {ITestSharefileResult} from 'types/TestSharefileResult';
import {TransportType, TransportTypeUnion} from 'types/TransportType';
import {LoaderInline} from 'components/Loader';
import {Transport} from 'types/transport/Abstract/AbstractTransport';
import {EmailSettings} from 'types/EmailSettings';
import {ResultContent as _ResultContent} from 'features/layout/CommonPage/panels/TransportPanel/components/common/ResultContent';
import produce from 'immer';
import {DirectoryListingProvider} from 'features/layout/CommonPage/panels/TransportPanel/components/common/DirectoryListingProvider';
// import {WINDOWS_SEP} from 'common/constants';
import {isString, isUndefined} from 'lodash';
import {concatPath} from 'common/stringUtils';
import {IDiplomatFile} from 'types/DiplomatFile';
import {transportDataGetterContext} from 'features/layout/CommonPage/panels/TransportPanel/components/common/transportDataGetterContext';
import {useIsTestPermitted} from './useIsTestPermitted';

type Props = {
   api: string;
   getData: () => Transport | {address: string; port: number} | EmailSettings;
   disabled: boolean;
   RenderLabels: React.ComponentType<{result: any}>;
   ResultContent?: React.ComponentType<{
      state?: any;
      error?: any;
      onlyLables?: boolean;
      loading?: boolean;
   }>;
   transportType: TransportTypeUnion;
   onlyLables?: boolean;
   selectPath?: (path: string) => void;
   directoriesOnly?: boolean;
};
export const AbstractTestTransport = React.memo(
   ({
      api,
      getData,
      disabled,
      transportType,
      RenderLabels,
      ResultContent = _ResultContent,
      onlyLables,
      selectPath,
      directoriesOnly = true,
   }: Props) => {
      const {t} = useTranslation();
      const {open, handleOpen, handleClose} = useModal();
      const {request, state: _state, loading, error} = usePostApi<ITestSharefileResult>(api, {
         persistState: true,
         onSuccess: handleOpen,
      });
      const {partner, anonymous} = React.useContext(transportDataGetterContext);
      const nonAnonymWithinTransaction = !partner && !anonymous;

      const state = React.useMemo(() => {
         return produce(_state, (draft) => {
            draft?.directoryContents?.forEach(
               (d) =>
                  draft.dirPath && d.name === d.path && (d.path = concatPath(draft.dirPath, d.path))
            );
            return draft;
         });
      }, [_state]);

      const {dirPath} = state || {};

      const [path, setPath] = React.useState(dirPath);
      const [selected, setSelected] = React.useState<IDiplomatFile>();

      const handleTest = React.useCallback(
         (dirPath?: string) => {
            let data = getData();
            if (isString(dirPath)) {
               data = produce(data, (draft: Transport) => {
                  draft.server && (draft.server.directory = dirPath);
                  return draft;
               });
            }
            request(JSON.stringify(data), {
               onSuccess: () => {
                  dirPath && setPath(dirPath);
               },
            });
         },
         [request, getData]
      );

      const typeName = TransportType[transportType].display;

      const isPermitted = useIsTestPermitted();
      // console.debug(state);

      return !isPermitted ? null : (
         <div>
            <Button
               disabled={disabled || loading}
               onClick={() => handleTest()}
               type="button"
               title={t(`Test ${typeName} connection`)}
            >
               {t('Test')} {loading ? <LoaderInline /> : null}
            </Button>

            <Dialog
               maxWidth={'md'}
               open={open}
               close={handleClose}
               header={
                  <>
                     {t(`${typeName} Test Results`)}{' '}
                     {loading && <LoaderInline size="1.5rem" color="warning" />}
                  </>
               }
            >
               <DialogContent>
                  {!isUndefined(state) && <RenderLabels result={state} />}
                  <DirectoryListingProvider
                     value={{
                        path,
                        request: handleTest,
                        selected,
                        setSelected,
                     }}
                  >
                     <ResultContent
                        loading={loading}
                        state={state}
                        error={error}
                        onlyLables={onlyLables}
                     />
                  </DirectoryListingProvider>
               </DialogContent>
               <DialogActions>
                  {loading && (
                     <LoaderInline
                        size="1.5rem"
                        color="primary"
                        style={{position: 'absolute', right: '3rem', bottom: '2.5rem'}}
                     />
                  )}
                  {selectPath && !nonAnonymWithinTransaction && (
                     <Button
                        disabled={!selected || (directoriesOnly && !selected?.directory)}
                        title={selected?.path}
                        onClick={() => {
                           selectPath(selected!.path);
                           handleClose();
                        }}
                     >
                        {t('Select')}
                     </Button>
                  )}
                  <Button onClick={handleClose}>{t('Close')}</Button>
               </DialogActions>
            </Dialog>
         </div>
      );
   }
);
